'use client';


/** Imports */
import Link from 'next/link';
import styles from './page.module.css';
import {useTranslations} from 'next-intl';
import {parseDurationMs} from 'ms-parse-duration';
import {useEffect, useState, type CSSProperties} from 'react';
import {LuExternalLink, LuArrowLeft, LuArrowRight, LuTriangleAlert, LuCirclePause} from 'react-icons/lu';


/** Constants */
const REDIRECT_DELAY_MS = parseDurationMs('5s');
const REDIRECT_DELAY_SECONDS = REDIRECT_DELAY_MS / parseDurationMs('1s');


/** Types */
type Props = Readonly<{
    target: string;
    autoRedirectEnabled: boolean;
}>;


/** Helpers */
function getSafeRedirectTarget(target: string): string | null {
    try {
        const url = new URL(target);
        if ((url.protocol === 'http:') || (url.protocol === 'https:')) return target;
    } catch {
        return null;
    }
    return null;
}


/** Component */
export default function LeavingDialog(props: Props) {
    const {target, autoRedirectEnabled} = props;

    // Hooks
    const t = useTranslations('common');
    const [millisecondsRemaining, setMillisecondsRemaining] = useState(REDIRECT_DELAY_MS);
    const [redirectCanceled, setRedirectCanceled] = useState(false);

    // Constants
    const safeRedirectTarget = getSafeRedirectTarget(target);
    const secondsRemaining = Math.ceil(millisecondsRemaining / 1_000);
    const redirectProgress = 1 - (millisecondsRemaining / REDIRECT_DELAY_MS);
    const countdownStyle = {
        '--redirect-progress': redirectProgress,
    } as CSSProperties;
    const shouldShowAutomaticRedirect = Boolean(safeRedirectTarget && autoRedirectEnabled);
    const shouldRunAutomaticRedirect = (shouldShowAutomaticRedirect && !redirectCanceled);

    // Effects
    useEffect(() => {
        if (!shouldRunAutomaticRedirect || !safeRedirectTarget) return;

        setMillisecondsRemaining(REDIRECT_DELAY_MS);
        const redirectAt = Date.now() + REDIRECT_DELAY_MS;
        const intervalId = window.setInterval(() => {
            const nextMillisecondsRemaining = Math.max(0, redirectAt - Date.now());
            setMillisecondsRemaining(nextMillisecondsRemaining);

            if (nextMillisecondsRemaining === 0) {
                window.clearInterval(intervalId);
                window.location.assign(safeRedirectTarget);
            }
        }, 100);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [safeRedirectTarget, shouldRunAutomaticRedirect]);

    // Event handler
    function handleBackClick() {
        if (window.history.length > 1) window.history.back();
        else window.close();
    }

    function handleCancelRedirect() {
        setRedirectCanceled(true);
    }


    // Render
    return (
        <section
            className={styles.leavingContainer}
            aria-labelledby="leaving-title"
            aria-describedby="leaving-description leaving-disclaimer"
        >
            <div className={styles.badge} aria-hidden="true">
                <LuTriangleAlert/>
            </div>
            <h1 className={styles.title} id="leaving-title">
                {t.rich('leavingDialog.title', {
                    MediaFox: (children) => <span className={styles.mediaFoxHighlight}>{children}</span>,
                })}
            </h1>
            <div className={styles.description} id="leaving-description">
                {t('leavingDialog.description')}
            </div>
            <div className={styles.url}>
                <div className={styles.icon} aria-hidden="true">
                    <LuExternalLink/>
                </div>
                <div className={styles.linkWrapper}>
                    <div className={styles.destinationLabel}>
                        {t('leavingDialog.destinationLabel')}
                    </div>
                    <div className={styles.link}>
                        {target}
                    </div>
                </div>
            </div>
            {shouldShowAutomaticRedirect && (
                <div
                    className={`${styles.timer} ${(redirectCanceled) ? styles.timerCanceled : ''}`}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <div
                        className={styles.timerProgress}
                        style={countdownStyle}
                        role="progressbar"
                        aria-label={t('leavingDialog.timerProgressLabel')}
                        aria-valuemin={0}
                        aria-valuemax={REDIRECT_DELAY_SECONDS}
                        aria-valuenow={REDIRECT_DELAY_SECONDS - secondsRemaining}
                        aria-valuetext={(redirectCanceled)
                            ? t('leavingDialog.timerCanceledDescription')
                            : t('leavingDialog.timerAriaLabel', {seconds: secondsRemaining})}
                    >
                        <span className={styles.timerSeconds} aria-hidden="true">
                            {secondsRemaining}
                        </span>
                    </div>
                    <div className={styles.timerCopy}>
                        <div className={styles.timerLabel}>
                            {(redirectCanceled)
                                ? t('leavingDialog.timerCanceledLabel')
                                : t('leavingDialog.timerLabel')}
                        </div>
                        <div className={styles.timerDescription}>
                            {(redirectCanceled)
                                ? t('leavingDialog.timerCanceledDescription')
                                : t('leavingDialog.timerDescription', {seconds: secondsRemaining})}
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.timerCancelButton}
                        onClick={handleCancelRedirect}
                        disabled={redirectCanceled}
                        aria-label={t('leavingDialog.cancelAutoRedirectButton')}
                        title={t('leavingDialog.cancelAutoRedirectButton')}
                    >
                        <LuCirclePause aria-hidden="true"/>
                    </button>
                </div>
            )}
            {(safeRedirectTarget && !autoRedirectEnabled) && (
                <div className={`${styles.timer} ${styles.timerDisabled}`} role="note">
                    <div className={styles.timerDisabledIcon} aria-hidden="true">
                        <LuCirclePause/>
                    </div>
                    <div className={styles.timerCopy}>
                        <div className={styles.timerLabel}>
                            {t('leavingDialog.timerDisabledLabel')}
                        </div>
                        <div className={styles.timerDescription}>
                            {t('leavingDialog.timerDisabledDescription')}
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.buttons}>
                <button className="button low-cta" onClick={handleBackClick}>
                    <LuArrowLeft/> {t('leavingDialog.cancelButton')}
                </button>
                {(safeRedirectTarget)
                    ? (
                        <Link
                            className="button"
                            href={safeRedirectTarget}
                            rel="noopener noreferrer"
                        >
                            {t('leavingDialog.proceedButton')}
                            <LuArrowRight/>
                        </Link>
                    )
                    : (
                        <button
                            className="button --disabled"
                            disabled
                            aria-describedby="leaving-proceed-unavailable"
                        >
                            {t('leavingDialog.proceedButton')}
                            <LuArrowRight/>
                        </button>
                    )}
            </div>
            {!safeRedirectTarget && (
                <span id="leaving-proceed-unavailable" className={styles.visuallyHidden}>
                    {t('leavingDialog.proceedUnavailableDescription')}
                </span>
            )}
            <div className={styles.disclaimer} id="leaving-disclaimer">
                {t('leavingDialog.disclaimerText')}
            </div>
        </section>
    );
}
